import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { FilialFormDto, type FilialFormInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FilialService } from '@zaoblako/web/core/services/finance/filial.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ColorPickerComponent } from '@zaoblako/web/shared/dumbs/color-picker/color-picker.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-filial-form',
  templateUrl: './filial-form.component.html',
  styleUrls: ['./filial-form.component.scss'],
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    FormsModule,
    TagModule,
    ButtonComponent,
    SpinnerComponent,
    DirectoryCommonHistoryComponent,
    ColorPickerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilialFormComponent extends BaseFormComponent<FilialFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public filial: FilialFindInterface = <FilialFindInterface>{};

  public dto = FilialFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly filialService: FilialService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.filialService
      .findById<FilialFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, { withDeleted: true })
      .subscribe({
        next: (filial) => {
          this.filial = filial;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveFilial() {
    this.normalizeFilialColor();
    if (this.checkValidate(this.filial).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.filialService.update<FilialFormInterface, FilialFindInterface>(this.filial).subscribe({
          next: (filial) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Филиал "${this.filial.name}" обновлен`);
            this.formStoreService.saveNext(filial, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.filialService.create<FilialFindInterface>(this.filial).subscribe({
          next: (filial) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Филиал создан');
            this.formStoreService.saveNext(filial, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteFilial() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить филиал "${this.filial.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.filialService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Филиал "${this.filial.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreFilial() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить филиал "${this.filial.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.filialService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.filial = { ...this.filial, deleted: false };
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Филиал "${this.filial.name}" восстановлен`);
            this.formStoreService.saveNext(this.filial, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public changeColor(color: string | null | undefined): void {
    const value = color?.trim() ?? '';
    if (!value) {
      this.filial.color = null;
      this.cdRef.markForCheck();
      return;
    }

    this.filial.color = value[0] !== '#' ? `#${value}` : value;
    this.cdRef.markForCheck();
  }

  public onColorPickerChange(color: string): void {
    const value = color?.trim() ?? '';
    if (!value) {
      this.filial.color = null;
    } else {
      this.filial.color = value[0] !== '#' ? `#${value}` : value;
    }
    this.cdRef.markForCheck();
  }

  private normalizeFilialColor(): void {
    const trimmed = this.filial.color?.trim();
    if (!trimmed) {
      this.filial.color = null;
      return;
    }

    this.filial.color = trimmed[0] !== '#' ? `#${trimmed}` : trimmed;
  }
}
